import { View } from '@shared/components/View';
import { RelatedLookupField } from '@shared/data/entity/fields/RelatedLookupField';
import { LazyRelatedLookupOverflowMenu } from '@shared/scenes/entity/components/fields/RelatedCardLookupEditField/LazyRelatedLookupOverflowMenu';
import { getComponentLabel, isRelatedCardLookupComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { AddressCollectionViewField } from './AddressCollectionViewField';
import { FieldStyles } from './CardCollectionViewField';
import { DateCollectionViewField } from './DateCollectionViewField';
import { EmailCollectionViewField } from './EmailCollectionViewField';
import { NumberCollectionViewField } from './NumberCollectionViewField';
import { TagCollectionViewField } from './TagCollectionViewField';
import { TextCollectionViewField } from './TextCollectionViewField';

interface Props {
  field: RelatedLookupField;
  component: API.AnyComponent;
  workflowTemplateId: string;
  fieldStyles: FieldStyles;
}

export function RelatedLookupCollectionViewField({
  field,
  component,
  workflowTemplateId,
  fieldStyles,
}: Props) {
  if (field.isEmpty) {
    return null;
  }

  const extraCount = field.count - 1;
  const showOverflow = extraCount > 0 && isRelatedCardLookupComponent(component);

  return (
    <View row gap={0.5} centerVertically>
      <LeafField field={field} fieldStyles={fieldStyles} />
      {showOverflow && (
        <View stopPropagation>
          <LazyRelatedLookupOverflowMenu
            extraCount={extraCount}
            component={component}
            relatedField={field.relatedField}
            workflowTemplateId={workflowTemplateId}
            pillTextStyle={fieldStyles.relatedCardPillTextStyle}
            title={`${getComponentLabel(component)} (${field.count})`}
          />
        </View>
      )}
    </View>
  );
}

function LeafField({
  field,
  fieldStyles,
}: {
  field: RelatedLookupField;
  fieldStyles: FieldStyles;
}) {
  const leafField = field.firstLeafField;
  if (leafField === null) {
    return null;
  }

  switch (leafField.fieldType) {
    case 'AddressField':
      return (
        <AddressCollectionViewField
          field={leafField}
          style={fieldStyles.addressStyle}
          textStyle={fieldStyles.addressTextStyle}
        />
      );
    case 'DateField':
      return (
        <DateCollectionViewField
          field={leafField}
          style={fieldStyles.dateStyle}
          textStyle={fieldStyles.dateTextStyle}
        />
      );
    case 'EmailField':
      return (
        <EmailCollectionViewField
          field={leafField}
          style={fieldStyles.emailStyle}
          textStyle={fieldStyles.textStyle}
        />
      );
    case 'NumberField':
      return (
        <NumberCollectionViewField
          field={leafField}
          style={fieldStyles.numberStyle}
          textStyle={fieldStyles.textStyle}
        />
      );
    case 'TagField':
      return (
        <TagCollectionViewField
          field={leafField}
          style={fieldStyles.tagStyle}
          tagItemStyle={fieldStyles.tagItemStyle}
          tagTextStyle={fieldStyles.tagTextStyle}
        />
      );
    case 'TextField':
      return (
        <TextCollectionViewField
          field={leafField}
          style={fieldStyles.textFieldStyle}
          textStyle={fieldStyles.textStyle}
          numberOfLines={fieldStyles.textNumberOfLines}
        />
      );
    default:
      return null;
  }
}
